import React, { useState } from 'react';
import { Box, Button, Checkbox, Menu, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors } from '../../styles/theme';
import { CAD_PARCEL_STOCK_STYLE } from '../styles/mapTheme';
import {
  STOCK_STATUS_LABELS,
  STOCK_STATUSES,
} from '../utils/listingCoreConstants';
import { normalizeStockStatusFilter } from '../utils/recordStockStatus';
import {
  showroomCompactMenuButtonSx,
  showroomMenuItemSx,
  showroomMenuPaperSx,
} from '../../Showroom/Showroom.styles';

const STATUS_CHECKBOX_SX = {
  p: 0,
  color: colors.textSecondary,
  '&.Mui-checked': { color: colors.primary },
};

/**
 * Filtro compacto «Estado:» (multi), el mismo de las listas CAD.
 *
 * @param {{ value?: string|string[]|null, onChange?: (next: string[]|null) => void,
 *   counts?: Record<string, number>, totalCount?: number }} props
 */
export default function StockStatusFilter({
  value = null,
  onChange,
  counts = {},
  totalCount = 0,
}) {
  const [anchor, setAnchor] = useState(null);
  const selected = normalizeStockStatusFilter(value);
  const countOf = (status) => counts?.[status] || 0;
  const statusLabel = (() => {
    if (!selected) return `Todos · ${totalCount}`;
    if (selected.length === 1) {
      return `${STOCK_STATUS_LABELS[selected[0]] || selected[0]} · ${countOf(selected[0])}`;
    }
    const total = selected.reduce((acc, status) => acc + countOf(status), 0);
    return `${selected.length} estados · ${total}`;
  })();

  const toggleStatus = (status) => {
    const current = selected || [];
    const next = current.includes(status)
      ? current.filter((item) => item !== status)
      : [...current, status];
    onChange?.(next.length ? next : null);
  };

  return (
    <>
      <Button
        size="small"
        onClick={(event) => setAnchor(event.currentTarget)}
        endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
        aria-label="Filtrar por estado"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={showroomCompactMenuButtonSx}
        data-testid="stock-status-filter"
      >
        Estado:
        {' '}
        <Box
          component="span"
          sx={{ color: colors.textPanelTitle, fontWeight: 500, ml: 0.5 }}
        >
          {statusLabel}
        </Box>
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: showroomMenuPaperSx }}
      >
        <MenuItem
          selected={!selected}
          onClick={() => onChange?.(null)}
          sx={showroomMenuItemSx}
        >
          Todos · {totalCount}
        </MenuItem>
        {STOCK_STATUSES.map((status) => (
          <MenuItem
            key={status}
            selected={Boolean(selected?.includes(status))}
            onClick={() => toggleStatus(status)}
            sx={showroomMenuItemSx}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={Boolean(selected?.includes(status))}
                tabIndex={-1}
                disableRipple
                sx={STATUS_CHECKBOX_SX}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: CAD_PARCEL_STOCK_STYLE.stockFill[status],
                  flexShrink: 0,
                }}
              />
              {STOCK_STATUS_LABELS[status]} · {countOf(status)}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
